import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../lib/ui";

const PILLARS = [
  {
    n: "01",
    title: "Futsal first",
    body:
      "Brazil's 5-a-side game, futsal, took hold in the 1930s and 40s and became the country's real academy. A smaller, heavier ball on a hard court forces close control, quick decisions and hundreds more touches per session than eleven-a-side. Pelé, Zico, Ronaldinho, Ronaldo and Neymar all came through it.",
  },
  {
    n: "02",
    title: "Pelada on any surface",
    body:
      "Street and beach football — pelada — meant no one waited for a pitch, a kit or a coach. Play happened on concrete, sand and dirt, in tight spaces, with mixed ages and improvised rules. Skill was invented under pressure rather than drilled in isolation.",
  },
  {
    n: "03",
    title: "Factories and clubs as the ladder",
    body:
      "From Bangu onwards, workplaces and neighbourhood clubs gave poor players a route in: a job, a pitch and a competitive fixture list. Professionalisation in 1933 turned that route into a career, and by 1958 the national team was a portrait of the whole country.",
  },
  {
    n: "04",
    title: "Small courts, huge coverage",
    body:
      "A futsal court costs a fraction of a full pitch, needs no irrigation and survives daily use. One court can serve a whole estate morning to night, which is why Brazil could scale participation faster than it could ever build stadiums.",
  },
];

const KENYA = [
  ["Hard-court futsal boxes in every estate", "Cheap to build, floodlit, usable all year, and safe for girls and boys to train late."],
  ["Coach the small game first", "Under-12 development on 5-a-side courts before eleven-a-side, so technique is built by volume of touches."],
  ["A real fixture ladder", "Estate leagues feeding county leagues feeding the national pathway, with results published and scouted."],
  ["Community ownership", "Courts held by the community with clear governance, so facilities survive beyond the funding cycle."],
];

export const BrazilBlueprint = () => (
  <section className="relative overflow-hidden border-t border-white/10 bg-black py-20 md:py-28" data-testid="home-brazil">
    <div className="pointer-events-none absolute inset-0 shuka-check opacity-60" aria-hidden="true" />
    <div className="relative mx-auto max-w-[1500px] px-5 md:px-10">
      <Reveal>
        <p className="eyebrow">The blueprint</p>
        <h2 className="display-2 mt-5 max-w-3xl">What Brazil built on concrete</h2>
        <p className="body-copy mt-6 max-w-2xl">
          Brazil did not become the country of football by building stadiums first. It built a habit — a small-sided,
          everyday, everywhere game — and the talent followed. Kenya can copy the method, not the myth.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2">
        {PILLARS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.06} className="bg-[#0b0b0b] p-8 md:p-10">
            <div data-testid={`brazil-pillar-${p.n}`}>
              <span className="mono text-sm text-maasai">{p.n}</span>
              <h3 className="mt-4 font-display text-xl font-semibold uppercase tracking-tight text-white md:text-2xl">
                {p.title}
              </h3>
              <p className="body-copy mt-4">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.1fr]">
        <Reveal>
          <p className="eyebrow">Transferable to Kenya</p>
          <h3 className="display-3 mt-5 max-w-md">Four things we take from it</h3>
          <p className="body-copy mt-5 max-w-md">
            Our milestones are built around the same logic: cheap surfaces, dense participation, and a ladder that
            a kid in Kibera can actually climb.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/plans" className="btn-primary" data-testid="brazil-plans-cta">
              The milestones
            </Link>
            <a
              href="https://en.wikipedia.org/wiki/History_of_football_in_Brazil"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid="brazil-source-link"
            >
              Source: Wikipedia <ArrowUpRight size={14} />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="border-t border-white/10">
            {KENYA.map(([title, body], i) => (
              <li key={title} className="border-b border-white/10 py-6" data-testid={`brazil-lesson-${i}`}>
                <div className="flex items-start gap-5">
                  <span className="mono mt-1 shrink-0 text-xs text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-display text-lg uppercase tracking-tight text-white">{title}</p>
                    <p className="body-copy mt-2">{body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);
